/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unknown-property */
import {
  ActionProps,
  Badge,
  BadgeColor,
  BadgeEmphasis,
  Icon, ICON_CHECK, ICON_CLOSE, ICON_CUSTOM_ALERT, ICON_IMAGE_DRAG, ICON_IMAGE_OUTLINE, ICON_MAGNIFY, ICON_PLUS, ICON_VIDEO_OUTLINE, Image,
} from '@doveit/bricks';
import React from 'react';
import { uniqueId } from 'lodash';
import * as styles from './MultimediaContentThumbnail.style';
import { ContentType } from '../../domain/types';

type MultimediaContentOperation = 'view' | 'drag' | 'add' | 'remove';

export interface MultimediaContentThumbnailProps extends React.AriaAttributes {
  index?: number,
  type?: ContentType.FOTO | ContentType.VIDEO,
  operation?: MultimediaContentOperation,
  picture?: {
    src: string,
    alt: string,
  },
  aspectRatio?: '1 / 1' | '4 / 3' | '16 / 9' | '3 / 2',
  active?: boolean,
  selected?: boolean,
  disabled?: boolean,
  interactive?: boolean,
  isRemoving?: boolean,
  placeholderIcon?: string,
  hasError?: boolean,
  hasWarning?: boolean,
  /** When set, shows a small badge in the bottom-left corner, colored according to the thumbnail state */
  badgeLabel?: string,
  onClick?: VoidFunction,
  actions?: React.ReactElement<ActionProps>[],
}

const mapIconToOperation: { [key in MultimediaContentOperation]: string } = {
  view: ICON_MAGNIFY,
  drag: ICON_IMAGE_DRAG,
  add: ICON_PLUS,
  remove: ICON_CLOSE,
};

export const getBadgeAppearance = ({
  active, selected, disabled, hasError, hasWarning,
}: Pick<MultimediaContentThumbnailProps, 'active' | 'selected' | 'disabled' | 'hasError' | 'hasWarning'>): { color: BadgeColor, emphasis: BadgeEmphasis } => {
  if (disabled) return { color: 'neutral', emphasis: 'low' };
  if (active) return { color: 'primary', emphasis: 'high' };
  if (hasError) return { color: 'critical', emphasis: 'high' };
  if (hasWarning) return { color: 'warning', emphasis: 'high' };
  if (selected) return { color: 'primary', emphasis: 'low' };

  return { color: 'neutral', emphasis: 'low' };
};

const MultimediaContentThumbnail: React.FC<MultimediaContentThumbnailProps> = ({
  index,
  type = ContentType.FOTO,
  operation = 'view',
  aspectRatio = '1 / 1',
  picture,
  active = false,
  selected = false,
  interactive = false,
  isRemoving = false,
  disabled = false,
  hasError,
  hasWarning,
  badgeLabel,
  onClick,
  placeholderIcon,
  actions = [],
  'aria-label': ariaLabel = 'multimedia-content-thumbnail',
  ...rest
}) => (
  <div
    {...rest}
    css={styles.base}
    $isRemoving={isRemoving}
    onClick={onClick}
    aria-label={ariaLabel}
    $aspectRatio={aspectRatio}
    $disabled={disabled}
    role={onClick ? 'button' : undefined}
  >
    <div css={styles.placeholder}>
      <Icon
        path={placeholderIcon || (type === ContentType.FOTO ? ICON_IMAGE_OUTLINE : ICON_VIDEO_OUTLINE)}
        size={32}
      />
    </div>
    <div
      css={styles.content}
      $hasPicture={!!picture}
    >
      {picture && (
        <Image
          aria-label={`${ariaLabel} (picture)`}
          src={picture.src}
          alt={picture.alt}
          lazyLoad
          responsive
        />
      )}
    </div>
    <div
      css={styles.frame}
      $operation={operation}
      $interactive={!!onClick || interactive}
      $hasPicture={!!picture}
      $active={active}
      $hasError={hasError}
      $hasWarning={hasWarning}
    >
      {(hasError || hasWarning) && (
        <div
          css={styles.alertIcon}
          $hasError={hasError}
          aria-label={hasError ? 'error icon' : 'warning icon'}
        >
          <Icon
            path={ICON_CUSTOM_ALERT}
            size={18}
          />
        </div>
      )}

      {badgeLabel && (
        <div
          css={styles.badge}
          $hasAlertIcon={!!(hasError || hasWarning)}
          title={badgeLabel}
        >
          <Badge
            aria-label={`${ariaLabel} (badge)`}
            size="XXS"
            label={badgeLabel}
            {...getBadgeAppearance({
              active, selected, disabled, hasError, hasWarning,
            })}
          />
        </div>
      )}

      {selected && (
        <div
          css={styles.activeIcon}
          aria-label="active icon"
          $disabled={disabled}
        >
          <Icon
            path={ICON_CHECK}
            size={16}
          />
        </div>
      )}

      {actions.length > 0 && (
        <div
          css={styles.actions}
          aria-label={`${ariaLabel} (actions)`}
          onClick={(e) => e.stopPropagation()}
        >
          {actions.map((action) => (
            <div key={uniqueId('action-')}>
              {action}
            </div>
          ))}
        </div>
      )}

      <div
        css={styles.icon}
        $active={active}
        className="icon"
      >
        <Icon
          path={mapIconToOperation[operation]}
          size={18}
        />
      </div>
      {typeof index === 'number' && (
        <div
          aria-label={`${ariaLabel} (index)`}
          css={styles.index}
          className="index"
        >
          {index}
        </div>
      )}
    </div>
  </div>
);

export default MultimediaContentThumbnail;
